import * as React from 'react';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import DoNotStripSpacesSpan from '../common/DoNotStripSpacesSpan';
import TrainingSetTableRowActions from './TrainingSetTableRowActions';
import { ITrainingSet } from '../../types/my-types';
import { format } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useGetAllLanguages from '../hooks/languages/useGetAllLanguages';
import { LinkTableStyled } from '../common/StyledComponents';
import CertifiedBadge from '../common/CertifiedBadge';

import { AppContext } from '../../App';

export interface TrainingSetTableRowProps {
  trainingSet: ITrainingSet;
}

const TrainingSetTableRow: React.FC<TrainingSetTableRowProps> = ({ trainingSet }) => {
  const { dataPoolId, setToast } = React.useContext(AppContext);
  const { data: languages, isLoading: isLoadingLanguages } = useGetAllLanguages();
  const labelRef = React.useRef<HTMLAnchorElement>(null);
  const [labelTruncated, setLabelTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = labelRef.current;
    if (el) setLabelTruncated(el.scrollWidth > el.clientWidth);
  }, [trainingSet.label]);

  const languageDisplay = React.useMemo(() => {
    if (!trainingSet.language_code) return '—';
    if (isLoadingLanguages) return '...';
    const language = languages.find((l) => l.code === trainingSet.language_code);
    return language?.name || '...';
  }, [trainingSet.language_code, languages, isLoadingLanguages]);

  return (
    <TableRow data-cy={`tbl-row-training-set-${trainingSet.label}`}>
      <TableCell component="th" scope="row" sx={{ maxWidth: 250 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Tooltip title={labelTruncated ? trainingSet.label || '' : ''} placement="bottom">
            <LinkTableStyled
              ref={labelRef}
              to={`/data-pools/${dataPoolId}/train/${trainingSet.id}/details`}
              data-cy={`btn-training-set-details-${trainingSet.label}`}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                // Shrink to make room for the badge, but never grow — growing
                // would stretch the link across the cell and push the badge to
                // the far edge. min-width:0 is what lets it shrink at all, and
                // so keeps the ellipsis working.
                flex: '0 1 auto',
                minWidth: 0,
              }}
            >
              <DoNotStripSpacesSpan
                title={trainingSet.label || 'No Label'}
                cypressId={`lbl-training-set-name-row-${trainingSet.label}`}
              />
            </LinkTableStyled>
          </Tooltip>
          <CertifiedBadge
            certifiedAt={trainingSet.certified_at}
            certifiedBy={trainingSet.certified_by}
          />
        </div>
      </TableCell>
      <TableCell align="center">
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(trainingSet?.id || '');
            setToast({
              open: true,
              message: 'Copied!',
              variant: 'success',
            });
          }}
        >
          {trainingSet.id}
          <ContentCopyIcon style={{ fontSize: 14, color: '#5f6d7e' }} />
        </span>
      </TableCell>
      <TableCell align="center" sx={{ color: '#5f6d7e' }}>
        {languageDisplay}
      </TableCell>
      <TableCell align="center" sx={{ color: '#5f6d7e' }}>
        {trainingSet.documents.length}
      </TableCell>
      <TableCell align="center">
        {trainingSet.trained ? (
          <DoneIcon sx={{ fontSize: 18, color: '#66BBA9' }} />
        ) : (
          <span style={{ color: '#5f6d7e' }}>&mdash;</span>
        )}
      </TableCell>
      <TableCell align="center" sx={{ color: '#5f6d7e' }}>
        <Tooltip
          title={format(new Date(trainingSet.created_at), 'yyyy-MM-dd HH:mm:ss')}
          placement="top"
        >
          <span data-cy={`lbl-training-set-created-${trainingSet.label}`}>
            {format(new Date(trainingSet.created_at), 'yyyy-MM-dd')}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center" sx={{ color: '#5f6d7e' }}>
        <Tooltip
          title={`Updated: ${format(new Date(trainingSet.updated_at), 'yyyy-MM-dd HH:mm:ss')}`}
          placement="top"
        >
          <span>{format(new Date(trainingSet.updated_at), 'yyyy-MM-dd')}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <TrainingSetTableRowActions trainingSet={trainingSet} />
      </TableCell>
    </TableRow>
  );
};

export default TrainingSetTableRow;
